import { useEffect, lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from "react-router-dom";
import { useAppDispatch } from "./store/hooks";
import { login as loginAction } from "./store/authSlice";
import { userService } from "./api/services/user.service";

// Layouts - keep these as regular imports since they're used frequently
import Layout from "./layout/Layout";
import { UserLayout } from "./layout/UserLayout";
import { OnboardingLayout } from "./layout/OnboardingLayout";
import AddPropertyLayout from "./layout/AddPropertyLayout.tsx";
import InformationLayout from "./layout/InformationLayout.tsx";
import { PreviewLayout } from "./layout/PreviewLayout.tsx";

// Auth components - keep as regular imports
import { ProtectedRoute } from "./components/auth/ProtectedRoute.tsx";
import { AdminRoute } from "./components/auth/AdminRoute.tsx";

import Loading from "./components/Loading";
import ErrorBoundary, { RouteError } from "./components/ErrorBoundary";

// Loading component for Suspense fallback. Sized to sit inside the app shell
// (UserLayout's scroll area) without forcing an extra viewport of scroll.
const PageLoader = () => <Loading className="min-h-[60vh]" />;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `lazy()` that survives deploys. A failed chunk import is retried once (flaky
 * networks); if it still fails because the chunk no longer exists, the page reloads
 * once to pick up the new build (guarded in sessionStorage so it cannot loop). If
 * that doesn't help, the error reaches the route's branded `RouteError` page.
 */
function lazyWithRetry<T extends ComponentType<object>>(importer: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (firstError) {
      await wait(400);
      try {
        return await importer();
      } catch (error) {
        if (ErrorBoundary.isStaleChunkError(error) && ErrorBoundary.reloadForStaleChunk()) {
          // Keep Suspense showing the loader while the page reloads
          return new Promise<{ default: T }>(() => {});
        }
        throw error ?? firstError;
      }
    }
  });
}

// Lazy load all page components
// Landing & Public Pages
const LandingPage = lazyWithRetry(() => import("./pages/Landing/LandingPage"));
const AboutPage = lazyWithRetry(() => import("./pages/Landing/About"));
const ToolsForProsPage = lazyWithRetry(() => import("./pages/Landing/ToolsForProsPage"));
const LeadershipPage = lazyWithRetry(() => import("./pages/Landing/LeadershipPage"));
const NextDealPage = lazyWithRetry(() => import("./pages/Landing/FindNextDeal.tsx"));

// Onboarding Pages
const SigninPage = lazyWithRetry(() => import("./pages/Onboarding/SigninPage.tsx"));
const RegisterPage = lazyWithRetry(() => import("./pages/Onboarding/RegisterPage.tsx"));
const OnboardingPageOne = lazyWithRetry(() => import("./pages/Onboarding/OnboardingPageOne"));
const OnboardingPageTwo = lazyWithRetry(() => import("./pages/Onboarding/OnboardingPageTwo"));
const OnboardingPageThree = lazyWithRetry(() => import("./pages/Onboarding/OnboardingPageThree"));
const GetStarted = lazyWithRetry(() => import("./pages/GetStarted.tsx"));

// Dashboard & Main User Pages
const DashBoard = lazyWithRetry(() => import("./pages/DashBoard"));
const Inbox = lazyWithRetry(() => import("./pages/Inbox"));
const NotificationCenter = lazyWithRetry(() => import("./pages/Notification/NotificationCenter.tsx"));
const NotificationDetailPage = lazyWithRetry(() => import("./pages/Notification/NotificationDetailPage.tsx"));
const NotificationPreferences = lazyWithRetry(() => import("./pages/Settings/NotificationPreferences.tsx"));

// Profile Pages
const CreateProfile = lazyWithRetry(() => import("./pages/Profile/CreateProfile.tsx"));
const ProfilePage = lazyWithRetry(() => import("./pages/Profile/ProfilePage.tsx"));
const EditProfile = lazyWithRetry(() => import("./pages/Profile/EditProfile.tsx"));
const VerifyNow = lazyWithRetry(() => import("./pages/Profile/VerifyNow.tsx"));
const PublicProfilePage = lazyWithRetry(() => import("./pages/Profile/PublicProfilePage.tsx"));
const VerifyEmailPage = lazyWithRetry(() => import("./pages/VerifyEmail/VerifyEmailPage.tsx"));
const RequestPasswordResetPage = lazyWithRetry(() => import("./pages/PasswordReset/RequestPasswordResetPage.tsx"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/PasswordReset/ResetPasswordPage.tsx"));
const LenderApprovalPage = lazyWithRetry(() => import("./pages/LenderApproval/LenderApprovalPage.tsx"));
const BuyerReportPage = lazyWithRetry(() => import("./pages/BuyerReport/BuyerReportPage.tsx"));

// Contact Pages
const Contact = lazyWithRetry(() => import("./pages/Contact/Contact.tsx"));
const InviteContact = lazyWithRetry(() => import("./pages/Contact/InviteContact.tsx"));
const UploadContacts = lazyWithRetry(() => import("./pages/Contact/UploadContacts.tsx"));
const ContactDetailUpdate = lazyWithRetry(() => import("./pages/Contact/ContactDetailUpdate.tsx"));

// Buy Pages
const PropertyListings = lazyWithRetry(() => import("./pages/Buy/PropertyListings.tsx"));
const PropertyDetailsPage = lazyWithRetry(() => import("./pages/Buy/PropertyDetailsPage.tsx"));
const BuyingPreference = lazyWithRetry(() => import("./pages/Buy/BuyingPreference.tsx"));
const MyBuyBoxes = lazyWithRetry(() => import("./pages/Buy/MyBuyBoxes.tsx"));
const CreateBuyBox = lazyWithRetry(() => import("./pages/Buy/CreateBuyBox.tsx"));
const EditBuyBox = lazyWithRetry(() => import("./pages/Buy/EditBuyBox.tsx"));
const MatchedProperties = lazyWithRetry(() => import("./pages/Buy/MatchedProperties.tsx"));
const PropertyDetailsAdminView = lazyWithRetry(() => import("./pages/Buy/PropertyDetailsAdminView.tsx"));

// Sell Pages
const PropertyOverview = lazyWithRetry(() => import("./pages/Sell/PropertyOverview.tsx"));
const PropertyDetails = lazyWithRetry(() => import("./pages/Sell/PropertyDetails.tsx"));
const PropertyInspection = lazyWithRetry(() => import("./pages/Sell/PropertyInspection.tsx"));
const SuccessPage = lazyWithRetry(() => import("./pages/Sell/SuccessPage.tsx"));

// Listings Pages
const MyListings = lazyWithRetry(() => import("./pages/MyListings.tsx"));
const EditListing = lazyWithRetry(() => import("./pages/EditListing.tsx"));
const Marketplace = lazyWithRetry(() => import("./pages/Marketplace.tsx"));

// Bids Pages
const MyBids = lazyWithRetry(() => import("./pages/My-Bids/MyBids.tsx"));
const SellerCounter = lazyWithRetry(() => import("./pages/My-Bids/SellerCounter.tsx"));
const CounterBid = lazyWithRetry(() => import("./pages/My-Bids/CounterBid.tsx"));

// Offers Pages
const MyOffers = lazyWithRetry(() => import("./pages/Offers/MyOffers.tsx"));
const PropertyOffers = lazyWithRetry(() => import("./pages/Offers/PropertyOffers.tsx"));
const OfferDetails = lazyWithRetry(() => import("./pages/Offers/OfferDetails.tsx"));
const BuyerCounter = lazyWithRetry(() => import("./pages/Offers/BuyerCounter.tsx"));
const OfferCounterOfferPage = lazyWithRetry(() => import("./pages/Offers/OfferCounterOfferPage.tsx"));
const SellerBidDetail = lazyWithRetry(() => import("./pages/Offers/SellerBidDetail.tsx"));

// Informational Pages
const SellersPage = lazyWithRetry(() => import("./pages/Informational/SellersPage.tsx"));
const BuyersPage = lazyWithRetry(() => import("./pages/Informational/BuyersPage.tsx"));
const AgentsPage = lazyWithRetry(() => import("./pages/Informational/AgentsPage.tsx"));
const WholeSalersPages = lazyWithRetry(() => import("./pages/Informational/WholeSalersPages.tsx"));
const FAQs = lazyWithRetry(() => import("./pages/Informational/FAQs.tsx"));
const OurDifference = lazyWithRetry(() => import("./pages/OurDifference/OurDifference.tsx"));
const AboutUs = lazyWithRetry(() => import("./pages/AboutUs/AboutUs.tsx"));

// Legal Pages
const PrivacyPolicy = lazyWithRetry(() => import("./pages/Legal/PrivacyPolicy.tsx"));
const TermsOfService = lazyWithRetry(() => import("./pages/Legal/TermsOfService.tsx"));

// Admin Pages
const Dashboard = lazyWithRetry(() => import("./pages/Admin/Dashboard.tsx"));
const UserList = lazyWithRetry(() => import("./pages/Admin/Users/UserList.tsx"));
const UserDetail = lazyWithRetry(() => import("./pages/Admin/Users/UserDetail.tsx"));
const OrganizationList = lazyWithRetry(() => import("./pages/Admin/Organizations/OrganizationList.tsx"));
const OrganizationDetail = lazyWithRetry(() => import("./pages/Admin/Organizations/OrganizationDetail.tsx"));
const EditOrganizationTier = lazyWithRetry(() => import("./pages/Admin/Organizations/EditOrganizationTier.tsx"));
const VerificationQueuePage = lazyWithRetry(() => import("./pages/Admin/VerificationQueuePage.tsx"));
const VendorManagementPage = lazyWithRetry(() => import("./pages/Admin/Vendors/VendorManagementPage.tsx"));
const CommandLinksPage = lazyWithRetry(() => import("./pages/Admin/CommandLinks/CommandLinksPage.tsx"));
const FirstLookSettingsPage = lazyWithRetry(() => import("./pages/Admin/FirstLookSettings/FirstLookSettingsPage.tsx"));
const FirstLookReportsPage = lazyWithRetry(() => import("./pages/Admin/FirstLookReports/FirstLookReportsPage.tsx"));
const RatingsAdminPage = lazyWithRetry(() => import("./pages/Admin/Ratings/RatingsAdminPage.tsx"));
const CompaniesPage = lazyWithRetry(() => import("./pages/Admin/Companies/CompaniesPage.tsx"));
const MarketplaceSettingsPage = lazyWithRetry(() => import("./pages/Admin/Settings/MarketplaceSettingsPage.tsx"));
const AdminQueuesPage = lazyWithRetry(() => import("./pages/Admin/Queues/AdminQueuesPage.tsx"));
const LicenseQueuePage = lazyWithRetry(() => import("./pages/Admin/Queues/LicenseQueuePage.tsx"));
const AgentSitePage = lazyWithRetry(() => import("./pages/Affiliate/AgentSitePage.tsx"));
const AgentCatcherPage = lazyWithRetry(() => import("./pages/Affiliate/AgentCatcherPage.tsx"));
const AgentLeadConfirmPage = lazyWithRetry(() => import("./pages/Affiliate/AgentLeadConfirmPage.tsx"));
const AgentPageSettings = lazyWithRetry(() => import("./pages/Agent/AgentPageSettings.tsx"));
const AIDocumentQueuePage = lazyWithRetry(() => import("./pages/Admin/Verification/AIDocumentQueuePage.tsx"));
const DocumentReviewPage = lazyWithRetry(() => import("./pages/Admin/Verification/DocumentReviewPage.tsx"));

// Other Pages
const AffiliateLanding = lazyWithRetry(() => import("./pages/Affiliate/AffiliateLanding.tsx"));
const ProtectedPage = lazyWithRetry(() => import("./pages/Protected/ProtectedPage.tsx"));
const UnauthorizedPage = lazyWithRetry(() => import("./pages/Unauthorized/UnauthorizedPage.tsx"));

// Network Pages (from Epic6)
const MyNetworkPage = lazyWithRetry(() => import("./pages/Network/MyNetworkPage.tsx"));
const InviteBuyersPage = lazyWithRetry(() => import("./pages/Network/InviteBuyersPage.tsx"));
const ReferralFeesPage = lazyWithRetry(() => import("./pages/ReferralFees/ReferralFeesPage.tsx"));
const PreferredPartnersPage = lazyWithRetry(() => import("./pages/Network/PreferredPartnersPage.tsx"));

// Verification Pages (from Epic8)
const VerificationPage = lazyWithRetry(() => import("./pages/Verification/VerificationPage.tsx"));
const EntityDetailPage = lazyWithRetry(() => import("./pages/Verification/EntityDetailPage.tsx"));
const NewEntityPage = lazyWithRetry(() => import("./pages/Verification/NewEntityPage.tsx"));
const DocumentUploadPage = lazyWithRetry(() => import("./pages/Verification/DocumentUploadPage.tsx"));

// Deals Pages (from Epic7)
const MyDealsPage = lazyWithRetry(() => import("./pages/MyDealsPage.tsx"));

// Preview Pages
const PreviewIndex = lazyWithRetry(() => import("./pages/Preview/PreviewIndex.tsx"));
const CreateListingPreview = lazyWithRetry(() => import("./pages/Preview/CreateListingPreview.tsx"));
const ListingDetailPage = lazyWithRetry(() => import("./pages/Preview/ListingDetailPage.tsx"));

// Helper to wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

/** Every route gets the branded error page, so a failure never shows the router's developer screen. */
const withRouteErrors = (list: RouteObject[]): RouteObject[] =>
  list.map(
    (route) =>
      ({
        ...route,
        errorElement: route.errorElement ?? <RouteError />,
        ...(route.children ? { children: withRouteErrors(route.children) } : {}),
      }) as RouteObject
  );

const routes: RouteObject[] = [
  {
    path: "/",
    Component: Layout,
    children: [{ index: true, element: withSuspense(LandingPage) }],
  },
  {
    path: "/marketplace",
    element: withSuspense(Marketplace),
  },
  {
    path: "/users/:id",
    element: withSuspense(PublicProfilePage),
  },
  {
    path: "/login",
    element: withSuspense(LandingPage),
  },
  {
    path: "/signin",
    element: withSuspense(SigninPage),
  },
  {
    path: "/forgot-password",
    element: withSuspense(RequestPasswordResetPage),
  },
  {
    path: "/reset-password",
    element: withSuspense(ResetPasswordPage),
  },
  {
    // PRD 4.3 AC2: lender's magic link, no sign-in
    path: "/lender-approval/:token",
    element: withSuspense(LenderApprovalPage),
  },
  {
    path: "/register",
    element: withSuspense(RegisterPage),
  },
  {
    path: "/find-your-next-deal",
    element: withSuspense(NextDealPage),
  },
  {
    path: "/info",
    Component: InformationLayout,
    children: [
      { path: "seller", element: withSuspense(SellersPage) },
      { path: "buyer", element: withSuspense(BuyersPage) },
      { path: "agent", element: withSuspense(AgentsPage) },
      { path: "wholesaler", element: withSuspense(WholeSalersPages) },
      { path: "faqs", element: withSuspense(FAQs) },
      { path: "about-us", element: withSuspense(AboutUs) },
      { path: "our-difference", element: withSuspense(OurDifference) },
    ],
  },
  {
    path: "/get-started",
    element: withSuspense(GetStarted),
  },
  {
    path: "/onboarding",
    Component: OnboardingLayout,
    children: [
      { index: true, element: withSuspense(OnboardingPageOne) },
      {
        path: "page-two",
        element: withSuspense(OnboardingPageTwo),
      },
      {
        path: "page-three",
        element: withSuspense(OnboardingPageThree),
      },
    ],
  },
  {
    path: "/create-profile",
    Component: UserLayout,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <CreateProfile />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },

  {
    path: "/verify-email",
    element: withSuspense(VerifyEmailPage),
  },
  {
    path: "/profile",
    Component: UserLayout,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <ProfilePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "edit",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <EditProfile />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "verification",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <VerificationPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "verification/new-entity",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <NewEntityPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "verification/entities/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <EntityDetailPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "verification/entities/:id/upload",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <DocumentUploadPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/notifications",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <NotificationPreferences />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/user",
    Component: UserLayout,
    children: [
      { index: true, element: withSuspense(DashBoard) },
      {
        // PRD 4.4 AC3: full buyer report for sellers
        path: "buyer-report/:buyerId",
        element: withSuspense(BuyerReportPage),
      },
      {
        path: "buyer-report/bid/:bidId",
        element: withSuspense(BuyerReportPage),
      },
      {
        path: "verify-now",
        element: withSuspense(VerifyNow),
      },
      {
        path: "contact",
        children: [
          { index: true, element: withSuspense(Contact) },
          {
            path: "invite-contact",
            element: withSuspense(InviteContact),
          },
          {
            path: "import",
            element: withSuspense(UploadContacts),
          },
          {
            path: ":id",
            element: withSuspense(ContactDetailUpdate),
          },
        ],
      },
      {
        path: "inbox",
        element: withSuspense(Inbox),
      },
      {
        // Deep link into one bid's message thread (used by message notifications)
        path: "inbox/:bidId",
        element: withSuspense(Inbox),
      },
      {
        path: "notifications",
        element: withSuspense(NotificationCenter),
      },
      {
        path: "notifications/:id",
        element: withSuspense(NotificationDetailPage),
      },
      {
        path: "network",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MyNetworkPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "agent-page",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <AgentPageSettings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "network/invite",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <InviteBuyersPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        // PRD 7.2 / 7.4: the agent's negotiations and coaching report
        path: "referral-fees",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <ReferralFeesPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        // PRD 8.6 partner group, 8.5 closing alerts
        path: "network/partners",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PreferredPartnersPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/network",
    Component: UserLayout,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MyNetworkPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Buy section routes
  {
    path: "/buy",
    Component: UserLayout,
    children: [
      { index: true, element: withSuspense(PropertyListings) },
      {
        path: ":id",
        element: withSuspense(PropertyDetailsPage),
      },
      {
        path: "bids",
        element: withSuspense(MyBids),
      },
      {
        path: "bids/:id",
        element: withSuspense(MyBids),
      },
      {
        path: "buy-boxes",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MyBuyBoxes />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "buy-boxes/create",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <CreateBuyBox />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "buy-boxes/:id/edit",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <EditBuyBox />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "buy-boxes/:id/matches",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MatchedProperties />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "matches",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MatchedProperties />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "preference",
        element: withSuspense(BuyingPreference),
      },
      {
        path: "counter-bid",
        element: withSuspense(CounterBid),
      },
    ],
  },
  // Sell section routes
  {
    path: "/sell",
    Component: UserLayout,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MyListings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "offers",
        element: withSuspense(MyOffers),
      },
      {
        path: "offers/:id",
        element: withSuspense(PropertyOffers),
      },
      {
        path: "offers/:id/details",
        element: withSuspense(OfferDetails),
      },
      {
        path: ":id",
        element: withSuspense(PropertyDetailsPage),
      },
      {
        path: ":id/edit",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <EditListing />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "listing/:listingId/bid/:bidId",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <SellerBidDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "counter-offer",
        element: withSuspense(OfferCounterOfferPage),
      },
      {
        path: "buyer-counter",
        element: withSuspense(BuyerCounter),
      },
      {
        path: "seller-counter",
        element: withSuspense(SellerCounter),
      },
    ],
  },
  {
    path: "/sell/add",
    Component: UserLayout,
    children: [
      {
        element: <AddPropertyLayout />,
        children: [
          {
            index: true,
            element: withSuspense(PropertyOverview),
          },
          {
            path: "details",
            element: withSuspense(PropertyDetails),
          },
          {
            path: "inspection",
            element: withSuspense(PropertyInspection),
          },
          {
            path: "success",
            element: withSuspense(SuccessPage),
          },
        ],
      },
    ],
  },
  {
    path: "/deals",
    Component: UserLayout,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <MyDealsPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/about",
    element: withSuspense(AboutPage),
  },

  {
    path: "/tools-for-pros",
    element: withSuspense(ToolsForProsPage),
  },
  {
    path: "/our-leadership",
    element: withSuspense(LeadershipPage),
  },

  {
    path: "affiliate",
    element: withSuspense(AffiliateLanding),
  },
  // PRD 11.2 / 11.3: agent co-branded page and buyer catcher links (public)
  {
    path: "/a/:slug",
    element: withSuspense(AgentSitePage),
  },
  {
    path: "/a/:slug/catch/:template",
    element: withSuspense(AgentCatcherPage),
  },
  {
    path: "/a/:slug/confirm",
    element: withSuspense(AgentLeadConfirmPage),
  },
  {
    path: "/property-details-admin",
    element: withSuspense(PropertyDetailsAdminView),
  },
  {
    // Old standalone import page; the import now lives inside the app shell
    path: "/upload-contacts",
    element: <Navigate to="/user/contact/import" replace />,
  },
  {
    path: "/privacy-policy",
    element: withSuspense(PrivacyPolicy),
  },
  {
    path: "/terms-of-service",
    element: withSuspense(TermsOfService),
  },
  {
    path: "/protected",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <ProtectedPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: "/unauthorized",
    element: withSuspense(UnauthorizedPage),
  },
  {
    path: "/admin",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <Dashboard />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/users",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <UserList />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/users/:id",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <UserDetail />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/organizations",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <OrganizationList />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/organizations/:id",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <OrganizationDetail />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/organizations/:id/edit-tier",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <EditOrganizationTier />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/verification-queue",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <VerificationQueuePage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/approved-vendors",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <VendorManagementPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/command-links",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <CommandLinksPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/first-look-settings",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <FirstLookSettingsPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/first-look-reports",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <FirstLookReportsPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/ratings",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <RatingsAdminPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/queues",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <AdminQueuesPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/queues/licenses",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <LicenseQueuePage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/settings",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <MarketplaceSettingsPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/companies",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <CompaniesPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/ai-document-queue",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <AIDocumentQueuePage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/verification/documents/:documentId",
    element: (
      <AdminRoute>
        <Suspense fallback={<PageLoader />}>
          <DocumentReviewPage />
        </Suspense>
      </AdminRoute>
    ),
  },
  {
    path: "/preview",
    Component: PreviewLayout,
    children: [
      { index: true, element: withSuspense(PreviewIndex) },
      { path: "create-listing", element: withSuspense(CreateListingPreview) },
      { path: "listing-detail", element: withSuspense(ListingDetailPage) },
    ],
  },
  {
    // Unknown URLs get the branded "page not found" panel instead of the router's developer screen
    path: "*",
    loader: () => {
      throw new Response("Not Found", { status: 404 });
    },
  },
];

const router = createBrowserRouter(withRouteErrors(routes));

function App() {
  const dispatch = useAppDispatch();

  // Initialize auth state from cookie on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const profile = await userService.getProfile();

        // Restore Redux auth state from profile
        dispatch(
          loginAction({
            role: profile.role, // Use actual role (buyer/seller/agent)
            id: profile.flipiqAuthUserId, // Use FlipIQ Auth ID for owner comparison
            email: profile.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            activeOrgId: profile.activeOrganizationId,
            photoUrl: profile.photoUrl,
          })
        );
      } catch {
        // Silently fail - user is just not logged in
        console.log('No active session');
      }
    };

    initializeAuth();
  }, [dispatch]);

  return <RouterProvider router={router} />;
}

export default App;
